import { UPDropdownItem } from 'components/dropdown';
import { toInteger, toNumber } from 'lodash';
import { Document } from 'modules/documents/types';
import {
  APPLICANTS__STATUS_DISCARDED,
  APPLICANTS__STATUS_SUBMITTED,
  EMPLOYEE_STATUS__INVITED,
  EMPLOYEE_STATUS__NOT_ELIGIBLE,
  EMPLOYEE_STATUS__READY,
  EMPLOYEE_STATUS__TERMINATED,
} from 'translations/constants';
import { IEmployeeGridDetails } from 'types/employee';
import { ApplicantStatus } from 'types/employee/IJobApplicant';
import { ReactComponent as AppliedTagIcon } from '../../assets/icons/AppliedTagIcon.svg';
import { ReactComponent as DiscardedTagIcon } from '../../assets/icons/DiscardedTagIcon.svg';
import { ReactComponent as InvitedTagIcon } from '../../assets/icons/InvitedTagIcon.svg';
import { ReactComponent as ReadyTagIcon } from '../../assets/icons/ReadyTagIcon.svg';
import { ReactComponent as RequirementsTagIcon } from '../../assets/icons/RequirementsTagIcon.svg';
import { ReactComponent as TerminatedTagIcon } from '../../assets/icons/TerminatedTagIcon.svg';
import { StatusTagDescriptor } from './types';

export const generateEmployeesArray = (employees: IEmployeeGridDetails[]) => {
  const auxEmployees = employees.map((e): UPDropdownItem => {
    return { value: `${e.id}`, label: `${e.firstName} ${e.lastName}`, isDisabled: false };
  });
  return auxEmployees;
};

const generateINSSObject = (inssCode: string) => {
  const auxArray = inssCode.split('');

  const inssObj = {
    year: toNumber(auxArray[0] + auxArray[1]),
    month: toNumber(auxArray[2] + auxArray[3]),
    day: toNumber(auxArray[4] + auxArray[5]),
    genderCode: toNumber(auxArray[6] + auxArray[7] + auxArray[8]),
  };
  return inssObj;
};

export const treatAsUTC = (date: Date) => {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
  return result;
};

export const getDaysBetweenDates = (date1: Date, date2: Date) => {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const daysBetween = (treatAsUTC(date2).getTime() - treatAsUTC(date1).getTime()) / millisecondsPerDay;
  return daysBetween;
};

const generateYear = (year: string) => {
  const auxYear = year.split('');
  return toNumber(auxYear[2] + auxYear[3]);
};

const generateDateObject = (date: string) => {
  const auxDate = new Date(date);

  const dateObj = {
    year: generateYear(`${auxDate.getFullYear()}`),
    month: auxDate.getMonth() + 1,
    day: auxDate.getDate(),
  };
  return dateObj;
};

export const isINSSValid = (inss: string, gender: number) => {
  const auxINSSObj = generateINSSObject(inss);
  // [5838] If month is between 21-32, it's a temporary INSS with unknown gender (month + 20)
  // In this case, accept any gender code
  if (auxINSSObj.month >= 21 && auxINSSObj.month <= 32) {
    return true;
  }

  if (gender === 1 && auxINSSObj.genderCode % 2 === 0) {
    return false;
  }
  if (gender === 2 && auxINSSObj.genderCode % 2 === 1) {
    return false;
  }
  return true;
};

export const generateNumberOfPages = (itemsPerPage: number, total: number) => {
  let numberOfPages = toInteger(total / itemsPerPage);
  if (total % itemsPerPage) numberOfPages += 1;
  return numberOfPages;
};

export const generateAgeList = (minimum: number, maximum: number) => {
  const ageArray = [];
  for (let i = minimum; i <= maximum; ++i) {
    ageArray.push({ value: i, label: i });
  }
  return ageArray;
};

export const generateEmployeeDocumentForPeriodUpdate = (doc: any): Document => {
  return {
    file: null,
    id: 1,
    name: doc.name,
    type: toNumber(doc.category),
    error: {
      dateError: false,
      typeError: false,
      invalidDate: false,
    },
  };
};

export const isPersonalInfoError = (errors: any) => {
  return (
    !!errors.firstName ||
    !!errors.lastName ||
    !!errors.gender ||
    !!errors.dateOfBirth ||
    !!errors.birthplace ||
    !!errors.birthCountry ||
    !!errors.nationality ||
    !!errors.languageId ||
    !!errors.maritalStatus ||
    !!errors.inss
  );
};

export const isContactInfoError = (errors: any) => {
  return (
    !!errors.mobileNumber ||
    !!errors.emailAddress ||
    !!errors.iceMobileNumber ||
    !!errors.address?.city ||
    !!errors.address?.streetName ||
    !!errors.address?.streetNumber ||
    !!errors.address?.busNumber ||
    !!errors.address?.postalCode ||
    !!errors.address?.countryId ||
    !!errors.stayAddress?.city ||
    !!errors.stayAddress?.streetName ||
    !!errors.stayAddress?.streetNumber ||
    !!errors.stayAddress?.busNumber ||
    !!errors.stayAddress?.postalCode ||
    !!errors.stayAddress?.countryId ||
    !!errors.invalidEmail
  );
};

export const isWorkingInfoError = (errors: any) => {
  return (
    !!errors.workerclass ||
    !!errors.reasonForEmployment ||
    !!errors.yearsOfExperience ||
    !!errors.startDate ||
    !!errors.iban ||
    !!errors.bic ||
    !!errors.idCardNumber ||
    !!errors.modeOfTransport
  );
};

export const isAccountError = (errors: any) => {
  return (
    !!errors.idUploadURL ||
    !!errors.idUploadBackURL ||
    !!errors.idPeriodEnd ||
    !!errors.idPeriodStart ||
    !!errors.idPeriodInvalid ||
    !!errors.pictureUploadURL ||
    !!errors.password ||
    !!errors.notMatchingPassword ||
    !!errors.agreeGdpr ||
    !!errors.periodInvalid ||
    !!errors.proofOfInscriptionFile
  );
};

export const isIdPeriodValid = (startDate: Date, endDate: Date, atSave: boolean) => {
  const currentTime = new Date();

  if (!endDate || !startDate) {
    return !atSave;
  }

  const startDateAsString = typeof startDate === 'string' ? startDate : startDate.toDateString();
  const endDateAsString = typeof endDate === 'string' ? endDate : endDate.toDateString();

  if (startDateAsString === endDateAsString) {
    return false;
  }

  const startDateAsDate = new Date(startDateAsString);
  const endDateAsDate = new Date(endDateAsString);
  if (startDateAsDate > endDateAsDate || endDateAsDate < currentTime) {
    return false;
  }

  return true;
};

/**
 * The single status tag an employee card shows. The statuses are not mutually exclusive in the data
 * (a terminated employee can also be ineligible), so this is a priority chain, most final state
 * first: a terminated worker is terminated regardless of eligibility, and an invited one has no
 * eligibility verdict yet. Ready is the fallback, so a card always has exactly one tag.
 *
 * `isInvited` is passed in rather than derived here: the card already decides invited-ness (age === 0
 * on the grid payload) to choose between the contact block and the Resend/Copy buttons, and the tag
 * must agree with what is rendered next to it.
 */
export const getEmployeeStatusTag = (employee: IEmployeeGridDetails, isInvited: boolean): StatusTagDescriptor => {
  if (employee.terminated) return { labelKey: EMPLOYEE_STATUS__TERMINATED, tone: 'negative', icon: TerminatedTagIcon };
  if (isInvited) return { labelKey: EMPLOYEE_STATUS__INVITED, tone: 'neutral', icon: InvitedTagIcon };
  if (!employee.eligible)
    return { labelKey: EMPLOYEE_STATUS__NOT_ELIGIBLE, tone: 'warning', icon: RequirementsTagIcon };
  return { labelKey: EMPLOYEE_STATUS__READY, tone: 'neutral', icon: ReadyTagIcon };
};

/** Applicant equivalent. Only Submitted ("Applied" in the story) and Discarded exist, and the API
 * only ever returns those two, so an unknown value falls back to the Applied tag rather than
 * rendering an empty one. */
export const getApplicantStatusTag = (status: ApplicantStatus): StatusTagDescriptor =>
  status === ApplicantStatus.Discarded
    ? { labelKey: APPLICANTS__STATUS_DISCARDED, tone: 'negative', icon: DiscardedTagIcon }
    : { labelKey: APPLICANTS__STATUS_SUBMITTED, tone: 'neutral', icon: AppliedTagIcon };
